import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { useUserAuth } from '../context/UserAuthContext';
import { toast } from 'sonner';
import { Loader2, UserPlus, ArrowLeft, Check } from 'lucide-react';

const LOGO_URL = "/liquicent-logo.png";

const UserSignup = () => {
  const navigate = useNavigate();
  const { signup } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    company: '',
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name || formData.full_name.length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email required';
    }
    
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain an uppercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    const result = await signup(formData);
    
    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/user/dashboard');
    } else {
      toast.error(result.error || 'Signup failed');
    }
    
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Password requirements check
  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20 px-6">
      <Helmet>
        <title>Create Account | Liquicent</title>
      </Helmet>
      
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <img 
              src={LOGO_URL} 
              alt="Liquicent" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-semibold text-[#0F172A]">Liquicent</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Create your account</h1>
          <p className="text-[#64748B]">Join Liquicent to deploy autonomous agents</p>
        </div>

        <Card 
          className="p-8 bg-white border border-[#E5E7EB] rounded-2xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-[#0F172A] text-sm font-medium">
                Full Name *
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                placeholder="John Doe"
              />
              {errors.full_name && <p className="text-red-500 text-xs">{errors.full_name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">
                Work Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                placeholder="john@company.com"
              />
              {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#0F172A] text-sm font-medium">
                Password *
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                placeholder="••••••••"
              />
              {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
              
              {/* Password requirements */}
              <div className="flex flex-wrap gap-3 mt-2">
                <span className={`text-xs flex items-center gap-1 ${passwordChecks.length ? 'text-[#2CB1A1]' : 'text-[#94A3B8]'}`}>
                  <Check className="w-3 h-3" /> 8+ characters
                </span>
                <span className={`text-xs flex items-center gap-1 ${passwordChecks.uppercase ? 'text-[#2CB1A1]' : 'text-[#94A3B8]'}`}>
                  <Check className="w-3 h-3" /> Uppercase
                </span>
                <span className={`text-xs flex items-center gap-1 ${passwordChecks.number ? 'text-[#2CB1A1]' : 'text-[#94A3B8]'}`}>
                  <Check className="w-3 h-3" /> Number
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-[#0F172A] text-sm font-medium">
                Company <span className="text-[#94A3B8] font-normal">(Optional)</span>
              </Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                placeholder="Acme Inc"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1A3A8F] hover:bg-[#142C6D] text-white py-6 text-base font-medium rounded-lg transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Account
                </>
              )}
            </Button>

            <p className="text-xs text-[#64748B] text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-[#1A3A8F] hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#1A3A8F] hover:underline">Privacy Policy</Link>.
            </p>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center">
            <p className="text-[#64748B] text-sm">
              Already have an account?{' '}
              <Link to="/user/login" className="text-[#1A3A8F] hover:text-[#142C6D] font-semibold">
                Sign in
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#64748B] hover:text-[#1A3A8F] text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserSignup;
